import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  bindSiteChannel,
  createChannel,
  deleteChannel,
  getChannel,
  getChannelDeliveries,
  listChannels,
  listSiteChannels,
  testChannel,
  unbindSiteChannel,
  updateChannel,
  type ChannelCreate,
  type ChannelUpdate,
  type DeliveryLog,
  type IncidentSeverity,
  type NotificationChannel,
  type SiteChannelBinding,
} from "@/lib/api";

export const channelsKeys = {
  all: ["channels"] as const,
  list: () => [...channelsKeys.all, "list"] as const,
  detail: (id: number) => [...channelsKeys.all, "detail", id] as const,
  bySite: (siteId: number) =>
    [...channelsKeys.all, "by-site", siteId] as const,
  deliveries: (onlyFailures: boolean) =>
    [...channelsKeys.all, "deliveries", onlyFailures] as const,
};

export function useChannels(): UseQueryResult<NotificationChannel[], Error> {
  return useQuery({
    queryKey: channelsKeys.list(),
    queryFn: () => listChannels(),
  });
}

export function useChannel(
  id: number | null,
): UseQueryResult<NotificationChannel, Error> {
  return useQuery({
    queryKey: channelsKeys.detail(id ?? 0),
    queryFn: () => getChannel(id as number),
    enabled: id !== null && id !== undefined && id > 0,
  });
}

export function useSiteChannels(
  siteId: number | null,
): UseQueryResult<SiteChannelBinding[], Error> {
  return useQuery({
    queryKey: channelsKeys.bySite(siteId ?? 0),
    queryFn: () => listSiteChannels(siteId as number),
    enabled: siteId !== null && siteId !== undefined && siteId > 0,
  });
}

export function useCreateChannel(): UseMutationResult<
  NotificationChannel,
  Error,
  ChannelCreate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ChannelCreate) => createChannel(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: channelsKeys.all });
    },
  });
}

export function useUpdateChannel(): UseMutationResult<
  NotificationChannel,
  Error,
  { id: number; body: ChannelUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => updateChannel(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: channelsKeys.all });
    },
  });
}

export function useDeleteChannel(): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteChannel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: channelsKeys.all });
    },
  });
}

export function useTestChannel(): UseMutationResult<
  { ok: boolean; detail?: string },
  Error,
  number
> {
  return useMutation({
    mutationFn: (id: number) => testChannel(id),
  });
}

export function useChannelDeliveries(
  onlyFailures = false,
): UseQueryResult<DeliveryLog[], Error> {
  return useQuery({
    queryKey: channelsKeys.deliveries(onlyFailures),
    queryFn: () =>
      getChannelDeliveries({ only_failures: onlyFailures, limit: 50 }),
    refetchInterval: 20_000,
  });
}

export function useBindSiteChannel(): UseMutationResult<
  SiteChannelBinding,
  Error,
  { siteId: number; channelId: number; min_severity?: IncidentSeverity }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId, channelId, min_severity }) =>
      bindSiteChannel(siteId, channelId, { min_severity }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: channelsKeys.bySite(vars.siteId) });
    },
  });
}

export function useUnbindSiteChannel(): UseMutationResult<
  void,
  Error,
  { siteId: number; channelId: number }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId, channelId }) => unbindSiteChannel(siteId, channelId),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: channelsKeys.bySite(vars.siteId) });
    },
  });
}
